import React from 'react';
import { Bar } from '@ant-design/plots';
import { useSelector } from 'react-redux';
import { ApplicationState } from '../../../store';
import { maskCamelCase } from '../../../utils/masks';
import { Serie, SeriesGroup } from '../../../store/ducks/dashboard/types';
import DataSet from '@antv/data-set';
import EmptyChart from '../EmptyChart';

interface Props {
  title: string;
  data: any[];
  series: Serie[];
  xField: string;
  seriesGroup?: SeriesGroup[];
  yField: string;
  slider?: {
    start: number;
    end: number;
  };
  onClick(values: any): void;
}

const BarChart: React.FC<Props> = ({
  title,
  data,
  xField,
  yField,
  slider,
  seriesGroup,
  series,
  onClick
}) => {
  const { DataView } = DataSet;
  const getColor = () => {
    const colors = [
      '#5B8FF9',
      '#61DDAA',
      '#65789B',
      '#F6BD16',
      '#7262fd',
      '#78D3F8',
      '#9661BC',
      '#F6903D',
      '#008685',
    ];
    return seriesGroup && seriesGroup?.length > 0 ? colors : layout?.color.default;
  };
  const dv = new DataView().source(data);
  if (seriesGroup && seriesGroup?.length > 0) {
    const arr: string[] = [];
    const groups: any = {};
    seriesGroup.forEach((item, index) => {
      item?.series.forEach((serie) => arr.push(serie.xField?.fieldName || ''));
      item?.series.forEach((serie) =>
        Object.assign(groups, { [serie.xField?.fieldName || '']: `grupo${index}` })
      );
    });
    dv.transform({
      type: 'fold',
      fields: arr,
      key: 'stack',
      value: 'valor',
    });
    dv.transform({
      type: 'map',
      callback(row) {
        return {
          ...row,
          group: groups[row.stack],
        };
      },
    });
  } else {
    dv.transform({
      type: 'fold',
      fields: series.map((serie) => serie.xField?.fieldName || ''),
      key: 'category',
      value: 'valor',
    });
  }

  const layout = useSelector((state: ApplicationState) => state.layout.data);
  const config: any = {
    data: dv.rows,
    yField: !(seriesGroup && seriesGroup?.length > 0)
      ? yField
      : seriesGroup[0]?.series[0].yField?.fieldName || '',
    xField: 'valor',
    isStack: !!(seriesGroup && seriesGroup?.length > 0),
    isGroup: !!(seriesGroup && seriesGroup?.length > 0),
    seriesField: seriesGroup && seriesGroup?.length > 0 ? 'stack' : undefined,
    groupField: seriesGroup && seriesGroup?.length > 0 ? 'group' : undefined,
    color: getColor(),
    min: 0,
    xAxis: {
      label: {
        autoHide: true,
        autoRotate: true,
      },
    },
    slider: slider,
    tooltip:
      seriesGroup && seriesGroup?.length > 0
        ? {
            formatter: (datum: any) => ({
              name: maskCamelCase(datum.stack),
              value: datum.valor,
            }),
          }
        : undefined,
    label: {
      position: 'middle',
      layout: [
        {
          type: 'interval-adjust-position',
        },
        {
          type: 'interval-hide-overlap',
        },
        {
          type: 'adjust-color',
        },
      ],
    },
    meta: {
      [xField]: {
        alias: maskCamelCase(xField),
      },
      [yField]: {
        alias: maskCamelCase(yField),
      },
    },
    onReady: (plot: any) => {
      plot.on('element:click', ({ data: { data } }: any) => {
        onClick(data);
      });
    }
  };
  return data.length === 0 ? (
    <EmptyChart type={'bar'} />
  ) : (
    <div style={{ padding: '20px' }}>
      <h2 style={{ color: layout?.color.dark }}>{title}</h2>
      <Bar {...config} />
    </div>
  );
};

export default BarChart;
